import React from 'react'

import invariant from 'invariant'
import {matchPath, useLocation, useNavigate} from 'react-router'

import {useAppDispatch, useAppSelector} from '../hooks/redux'
import {openContactModal} from '../redux/features/modal'

import * as elevio from '../../../commons/src/services/elevio'
import useLocalStorageState from '../hooks/use-local-storage-state'
import {
  goToDiscounts,
  goToDynamicProductionTimes,
  goToInfills,
  goToInvoices,
  goToOrders,
  goToPricings,
  goToProductionTimes,
  goToProviderInfos,
  goToSettings,
  goToShippings,
  goToSignInAdmin,
} from '../services/navigation'
import {isPermitted} from './utils/routing'

import {InvoicesTab, PricingTab, SettingsTab} from '../types'

import alignLeftIcon from '../../../commons/src/assets/icons/align-left.svg'
import alignRightIcon from '../../../commons/src/assets/icons/align-right.svg'
import bankNoteIcon from '../../../commons/src/assets/icons/bank-note.svg'
import bankIcon from '../../../commons/src/assets/icons/bank.svg'
import cartIcon from '../../../commons/src/assets/icons/cart.svg'
import clockCheckIcon from '../../../commons/src/assets/icons/clock-check.svg'
import clockPlusIcon from '../../../commons/src/assets/icons/clock-plus.svg'
import helpCircleIcon from '../../../commons/src/assets/icons/help-circle.svg'
import infillIcon from '../../../commons/src/assets/icons/infill.svg'
import materialIcon from '../../../commons/src/assets/icons/material.svg'
import tagIcon from '../../../commons/src/assets/icons/tag.svg'
import truckIcon from '../../../commons/src/assets/icons/truck.svg'
import userCircleIcon from '../../../commons/src/assets/icons/user-circle.svg'
import usersIcon from '../../../commons/src/assets/icons/users.svg'

import Icon from '../../../commons/src/components/icon'
import Logo from '../components/logo'
import NavigationLayout from '../components/navigation-layout'
import NavigationDivider from '../components/navigation-layout/NavigationDivider'
import NavigationLink from '../components/navigation-layout/NavigationLink'

import ROUTES from '../routes'

import {routes} from './app'

interface Props {
  children: React.ReactNode
}

type NavigationItemOption = {
  href?: string
  action?: () => void
  icon?: string
  label: string
}

const PagePartial = ({children}: Props) => {
  const [collapse, setCollapse] = useLocalStorageState('nav_collapse', false)
  const routerLocation = useLocation()
  const navigate = useNavigate()

  const dispatch = useAppDispatch()
  const authUser = useAppSelector(state => state.auth.user)
  const isAdminMode = useAppSelector(state => state.auth.isAdminMode)

  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const getPermittedNavigationItems = (navigationItems: NavigationItemOption[]) =>
    navigationItems.filter(item => {
      if (!item.href) {
        return item
      }
      const route = routes.find(({path}) => !!item.href && path === item.href)
      const navigationItemPermissions = route?.requiredPermissions
      return !!authUser && isPermitted(authUser.permission, navigationItemPermissions)
    })

  const adminNavigationItems = [
    {
      action: () => {
        goToSignInAdmin(navigate)
      },
      icon: usersIcon,
      label: 'Switch Provider',
    },
  ]

  const navigationItems = [
    {
      href: ROUTES.ORDERS,
      action: () => goToOrders(navigate),
      icon: cartIcon,
      label: 'Orders',
    },
    {
      href: `${ROUTES.INVOICES}/*`,
      action: () => goToInvoices(navigate, {activeTab: InvoicesTab.V2}),
      icon: bankIcon,
      label: 'Earnings',
    },
  ]

  const catalogNavigationItems = [
    {
      href: ROUTES.PROVIDER_INFOS,
      action: () => goToProviderInfos(navigate),
      icon: materialIcon,
      label: 'Materials & Configurations',
    },
    {
      href: `${ROUTES.PRICINGS}/*`,
      action: () => goToPricings(navigate, {activeTab: PricingTab.THREE_D_PRINTING}),
      icon: bankNoteIcon,
      label: 'Pricing',
    },
    {
      href: ROUTES.INFILLS,
      action: () => goToInfills(navigate),
      icon: infillIcon,
      label: 'Infill options',
    },
    {
      href: ROUTES.PRODUCTION_TIMES,
      action: () => goToProductionTimes(navigate),
      icon: clockCheckIcon,
      label: 'Production time options',
    },
    {
      href: ROUTES.DYNAMIC_PRODUCTION_TIMES,
      action: () => goToDynamicProductionTimes(navigate),
      icon: clockPlusIcon,
      label: 'Dynamic production times',
    },
    {
      href: ROUTES.SHIPPINGS,
      action: () => goToShippings(navigate),
      icon: truckIcon,
      label: 'Shipping',
    },
    {
      href: ROUTES.DISCOUNTS,
      action: () => goToDiscounts(navigate),
      icon: tagIcon,
      label: 'Discounts',
    },
  ]

  const permittedCatalogNavigationItems = getPermittedNavigationItems(catalogNavigationItems)
  const permittedNavigationItems = getPermittedNavigationItems(navigationItems)

  const permittedNavigationItemsTop =
    permittedNavigationItems.length > 0 && permittedCatalogNavigationItems.length > 0
      ? permittedNavigationItems.concat([
          {
            // Divider
            label: 'Catalog',
          },
          ...permittedCatalogNavigationItems,
        ])
      : permittedNavigationItems.concat(permittedCatalogNavigationItems)

  const navigationItemsTop = isAdminMode
    ? [
        ...adminNavigationItems,
        {
          // Divider
          label: '',
        },
        ...permittedNavigationItemsTop,
      ]
    : permittedNavigationItemsTop

  const navigationItemsBottom = [
    {
      href: ROUTES.SETTINGS,
      action: () => goToSettings(navigate, {activeTab: SettingsTab.GENERAL}),
      icon: userCircleIcon,
      label: 'Profile',
    },
    {
      action: () => {
        if (elevio.isEnabled()) {
          elevio.show()
        } else {
          dispatch(openContactModal({contactOptions: ['email', 'form']}))
        }
      },
      icon: helpCircleIcon,
      label: 'Need help?',
    },
    {
      action: () => setCollapse(!collapse),
      icon: collapse ? alignRightIcon : alignLeftIcon,
      label: 'Hide Sidebar',
    },
  ]

  const renderNavigationItems = (navigationitems: NavigationItemOption[]) =>
    navigationitems.map(({href, action, icon, label}) => {
      if (!href && !action) {
        return <NavigationDivider key={label}>{label}</NavigationDivider>
      }
      return (
        <NavigationLink
          key={href}
          href={href}
          label={label}
          active={!!matchPath(`${href}/*`, routerLocation.pathname)}
          icon={<Icon source={icon} />}
          onClick={action ? () => action() : undefined}
        />
      )
    })

  return (
    <NavigationLayout
      logo={<Logo alt="Craftcloud®" invert />}
      navigationItemsTop={renderNavigationItems(navigationItemsTop)}
      navigationItemsBottom={renderNavigationItems(navigationItemsBottom)}
      collapse={collapse}
    >
      {children}
    </NavigationLayout>
  )
}

export default PagePartial
